import React from 'react';
import { format } from 'date-fns/esm';
import { useSelector } from 'react-redux';

import { DATE_FORMAT_EU_DOT, DATE_FORMAT_HOURS_MINUTES } from '../../../core/constants/date';
import { formatBookingDuration } from '../../../core/utils/date';
import { selectEquipmentById } from '../../../core/state/equipment/selectors';
import { RootState } from '../../../core/state/state';
import Booking from '../../../core/types/model/Booking';
import { BookingStatus } from '../../../core/enums/BookingStatus';
import deliveryZoneColorMap from '../../../core/constants/deliveryZoneColorMap';

import TD from '../../../components/table/TD/TD';
import TR from '../../../components/table/TR/TR';
import DeliveryZoneBadge from '../../components/delivery-zone-badge/DeliveryZoneBadge';
import BookingStatusBadge from '../../components/booking-status-badge/BookingStatusBadge';
import { Color } from '../../../core/enums/Color';
import Badge from '../../components/badge/Badge';
import BookingListDropdown from './BookingListDropdown';
import Tooltip from '../../../components/tooltip/Tooltip';
import { UserRoleType as Roles } from '../../../core/state/user/types';
import Role from '../../components/role/Role';
import { Size } from '../../../core/enums/Size';
import { ISO_DATE_REGEX } from '../../../core/constants/regex';

interface Props {
  booking: Booking;
  updateBookings: () => void;
  isPrinting?: boolean;
}

const BookingListRow: React.FC<Props> = ({ booking, updateBookings, isPrinting }) => {
  const equipment = useSelector((state: RootState) => selectEquipmentById(state, booking.transportEquipmentId));
  const isBlocked = booking.confirmationStatus === BookingStatus.Blocked;
  const deliveryZoneIndex = useSelector((state: RootState) => state.deliveryZone.byId[booking.deliveryZoneId].creationIndex);

  return (
    <TR
      key={booking.id}
      className={`booking-row ${isBlocked ? 'booking-row--blocked' : ''} ${
        booking.confirmationStatus === BookingStatus.Pending ? 'booking-row--pending' : ''
      } ${isPrinting ? '' : 'position-relative'}`}
    >
      <TD
        className={`booking-row__bordered booking-row__bordered--${deliveryZoneColorMap[deliveryZoneIndex]} ${isPrinting ? '' : 'position-relative'}`}
      >
        {isBlocked ? booking.createdBy ?? '' : booking.title}
      </TD>
      <TD>{`${format(
        booking.confirmationStatus === BookingStatus.Done ? new Date(booking.confirmedOn) : new Date(booking.date),
        DATE_FORMAT_EU_DOT
      )}`}</TD>
      <TD>
        <span className="text-nowrap">
          {booking.confirmationStatus === BookingStatus.Done
            ? format(new Date(booking.confirmedOn.replace(ISO_DATE_REGEX, '/')), DATE_FORMAT_HOURS_MINUTES)
            : formatBookingDuration(booking)}
        </span>
      </TD>
      <TD>
        <DeliveryZoneBadge isBlocked={isBlocked} labelSize={Size.Small} id={booking.deliveryZoneId} />
      </TD>
      <TD>
        <Tooltip text={booking.description} placement="top">
          <span className="booking-row__description text-truncate">{booking.description || '--'}</span>
        </Tooltip>
      </TD>
      <TD>{equipment?.name ?? '--'}</TD>
      <TD>{booking.contactPersonText ?? '--'}</TD>
      <TD>
        <Badge color={Color.Blue} label={booking.pin} boldLabel />
      </TD>
      <TD>
        <BookingStatusBadge
          rejectionReason={booking.confirmationStatus === BookingStatus.Canceled ? booking.cancellationReason : booking.rejectionReason}
          status={booking.confirmationStatus}
        />
      </TD>
      <TD>
        <Role inRoles={[Roles.ProjectAdmin, Roles.Subcontractor, Roles.SystemAdmin, Roles.ProjectParticipant]}>
          <BookingListDropdown fetchBookings={updateBookings} booking={booking} />
        </Role>
      </TD>
    </TR>
  );
};

export default BookingListRow;
